/* istanbul ignore file */

import { useActorRef, useSelector } from '@xstate/react';
import { type SnapshotFrom } from 'xstate';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { otaUpdateStateMachine } from './ota-update-state-machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useOtaUpdateMachine = () => {
  //
  // ─── Feature Flags ───────────────────────────────────────────────────

  const initialUpdateEventTimeout = useFeatureFlag(
    'OTA-update-in-app-checker-initial-check-timeout-ms',
  );
  const pollingInterval = useFeatureFlag('OTA-update-in-app-checker-polling-interval-ms');

  // ─── Machine ─────────────────────────────────────────────────────────

  const otaUpdateActorRef = useActorRef(otaUpdateStateMachine, {
    input: { initialUpdateEventTimeout, pollingInterval },
  });

  const { send } = otaUpdateActorRef;

  // ─── Derived Data ────────────────────────────────────────────────────

  const availableUpdate = useSelector(otaUpdateActorRef, getAvailableUpdate);

  // ─── Flags ───────────────────────────────────────────────────────────

  const updateState = useSelector(otaUpdateActorRef, getUpdateState);

  // ─────────────────────────────────────────────────────────────────────

  return { availableUpdate, updateState, send };
};

// ─── Selectors ───────────────────────────────────────────────────────────────

function getAvailableUpdate(snapshot: Snapshot) {
  return snapshot.context.availableUpdate;
}

function getUpdateState(snapshot: Snapshot) {
  return {
    isRestartingApp: snapshot.matches('restarting'),
    hasFailedToRestartApp: snapshot.matches('restartingFailed'),
    hasPendingUpdate: snapshot.matches('pendingUpdate'),
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Snapshot = SnapshotFrom<typeof otaUpdateStateMachine>;
